<div class="relative flex h-full min-h-0 w-full flex-col overflow-hidden">
  <div class="flex min-h-0 flex-1 flex-col overflow-y-auto p-4">
    <div class="mb-4 sm:flex sm:items-center">
      <div class="sm:flex-auto">
        <h1 class="text-base font-semibold text-primary">Content Library</h1>
        <p class="mt-1 text-sm text-secondary">Videos you have approved across all campaigns.</p>
      </div>
    </div>

    @if (loadingState === 'loading') {
    <div class="flex min-h-60 w-full items-center justify-center">
      <atom-loading-inline message="Loading approved videos…"></atom-loading-inline>
    </div>
    } @else if (loadingState === 'error') {
    <div class="flex min-h-60 w-full items-center justify-center">
      <app-empty-state title="Couldn’t load content library"
        description="Something went wrong while loading your approved videos. Try again in a moment.">
      </app-empty-state>
    </div>
    } @else if (videos().length > 0) {
    <ul role="list" class="grid grid-cols-2 gap-x-4 gap-y-8 pb-4 sm:grid-cols-3 sm:gap-x-6 lg:grid-cols-4 xl:grid-cols-5 xl:gap-x-8">
      @for (video of videos(); track video.id) {
      <li class="relative flex flex-col">
        <div
          class="group aspect-9/16 overflow-hidden rounded-lg bg-gray-200 focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 focus-within:ring-offset-gray-100 dark:bg-zinc-800">
          <atom-video-with-controls class="block h-full w-full" [video]="video"
            [videoFormat]="VideoFormatEnum.Vertical"></atom-video-with-controls>
        </div>

        <div class="mt-2 flex flex-row items-start justify-between gap-x-2">
          <div class="flex min-w-0 items-start">
            @if (video.creator.photoURL) {
            <img class="size-8 shrink-0 rounded-full bg-gray-200 ring-2 ring-white dark:bg-zinc-700 dark:ring-zinc-900"
              [src]="imageService.getImageSource(video.creator.photoURL, 'gallery')"
              [alt]="video.creator.firstName + ' ' + video.creator.lastName" />
            } @else {
            <div
              class="flex size-8 shrink-0 items-center justify-center rounded-full bg-gray-200 text-xs font-medium text-gray-600 ring-2 ring-white dark:bg-zinc-700 dark:text-zinc-300 dark:ring-zinc-900"
              aria-hidden="true">
              {{ video.creator.firstName.charAt(0) }}{{ creatorInitial(video.creator.lastName) }}
            </div>
            }
            <div class="min-w-0 pl-2">
              <p class="truncate text-xs font-medium text-primary">
                {{ video.creator.firstName }} {{ creatorInitial(video.creator.lastName) }}
              </p>
              @if (briefTitle(video); as title) {
              <p class="truncate text-xs text-secondary">{{ title }}</p>
              }
            </div>
          </div>

          <div class="flex shrink-0 gap-1">
            <button type="button" class="btn-fab btn-fab-sm btn-primary" aria-label="Download video"
              (click)="downloadContent(video)">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                stroke="currentColor" class="size-4" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round"
                  d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0-4.5-4.5M12 16.5V3" />
              </svg>
            </button>
            @if (video.briefId && video.creator.blazeFirebaseUid) {
            <button type="button" class="btn-fab btn-fab-sm btn-black" aria-label="View submission"
              (click)="goToViewSubmission(video.briefId, video.creator.blazeFirebaseUid)">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                stroke="currentColor" class="size-4" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
              </svg>
            </button>
            }
          </div>
        </div>
      </li>
      }
    </ul>
    } @else {
    <div class="flex min-h-60 w-full items-center justify-center">
      <app-empty-state title="No approved content… yet."
        description="Once you start approving content, it will appear here in one easy-to-view feed."
        [hasConfirmButton]="true" confirmButtonText="Post Brief"
        (buttonAction)="handleEmptyStateAction($event)">
      </app-empty-state>
    </div>
    }
  </div>

  @if (loadingState === 'loaded' && videos().length > 0) {
  <nav class="sticky bottom-0 z-10 border-t border-zinc-200 bg-white shadow dark:border-zinc-800 dark:bg-zinc-900"
    aria-label="Content library pagination">
    <div class="mx-auto px-2 sm:px-6 lg:px-8">
      <div class="relative flex h-16 items-center justify-between">
        <button type="button" (click)="nextPage(-1)" [disabled]="page() === 0"
          class="inline-flex items-center gap-x-1.5 rounded-md bg-primary px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-primary/85 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-transparent dark:disabled:bg-zinc-800">
          <svg class="-ml-0.5 size-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
            stroke-width="1.5" stroke="currentColor" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
          </svg>
          Prev
        </button>

        <p class="text-sm text-secondary" aria-live="polite">Page {{ page() + 1 }}</p>

        <button type="button" (click)="nextPage(1)" [disabled]="videos().length < pageSize"
          class="inline-flex items-center gap-x-1.5 rounded-md bg-primary px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-primary/85 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400 dark:disabled:bg-zinc-800 dark:disabled:text-zinc-500">
          Next
          <svg class="-mr-0.5 size-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
            stroke-width="1.5" stroke="currentColor" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
          </svg>
        </button>
      </div>
    </div>
  </nav>
  }
</div>
